/* eslint-disable */
import React, { useState, useCallback, useEffect } from 'react';
import { Shield, Eye, ChevronLeft, ChevronRight, Upload, Download, DollarSign, TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle, Clock, Users, Building, Briefcase, MessageSquare, HelpCircle, XCircle } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, AreaChart, Area } from 'recharts';

const INP2ExecutiveCyberBridge = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [presentationMode, setPresentationMode] = useState(false);
  const [viewMode, setViewMode] = useState('executive'); // 'executive', 'technical', 'edit'
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [showImportExportHelp, setShowImportExportHelp] = useState(false);

  // Company data
  const [companyData, setCompanyData] = useState({
    companyName: 'TechCorp Industries',
    industry: 'Technology',
    employees: 1250,
    revenue: 89000000 // $89M
  });

  // Editable data state
  const [editableData, setEditableData] = useState({
    riskData: [
      { 
        category: 'Phishing', 
        current: 7, 
        previous: 6, 
        trend: 'up', 
        status: 'Gap',
        financialRisk: 2400000,
        description: 'Email-based social engineering attacks targeting employee credentials'
      },
      { 
        category: 'Data Breach', 
        current: 8, 
        previous: 7, 
        trend: 'up', 
        status: 'Gap',
        financialRisk: 4800000,
        description: 'Unauthorized access to sensitive customer and business data'
      },
      { 
        category: 'Ransomware', 
        current: 6, 
        previous: 8, 
        trend: 'down', 
        status: 'Improving',
        financialRisk: 3200000,
        description: 'Malicious software that encrypts data and demands payment'
      },
      { 
        category: 'Insider Threats', 
        current: 5, 
        previous: 5, 
        trend: 'stable', 
        status: 'Complete',
        financialRisk: 1600000,
        description: 'Risks from employees, contractors, or business associates'
      },
      { 
        category: 'Supply Chain', 
        current: 9, 
        previous: 7, 
        trend: 'up', 
        status: 'Gap',
        financialRisk: 5400000,
        description: 'Third-party vendor and supplier security vulnerabilities'
      }
    ],
    
    metricsData: [
      { metric: 'Intrusions Blocked', value: 98, target: 95, industry: 87, unit: '%' },
      { metric: 'MFA Adoption', value: 94, target: 99, industry: 76, unit: '%' },
      { metric: 'Training Completion', value: 89, target: 95, industry: 71, unit: '%' },
      { metric: 'Patch Compliance', value: 95, target: 98, industry: 82, unit: '%' }
    ],
    
    attackTrendsData: [
      { month: 'Jan', attacks: 245, blocked: 243 },
      { month: 'Feb', attacks: 198, blocked: 196 },
      { month: 'Mar', attacks: 267, blocked: 264 },
      { month: 'Apr', attacks: 189, blocked: 187 },
      { month: 'May', attacks: 203, blocked: 201 },
      { month: 'Jun', attacks: 156, blocked: 153 }
    ],
    
    detectionResponseData: {
      meanTimeToDetect: 4,
      intrusionsBlocked: 98,
      meanTimeToRespond: 2,
      patchCompliance: 95,
      industryMTTD: 24,
      industryIntrusions: 87,
      targetMTTR: 4,
      targetPatching: 98
    },
    
    threatIntelligence: [
      {
        category: 'Ransomware Campaign Targeting Our Industry',
        technical: 'Advanced persistent threat using zero-day exploits in remote access tools',
        business: 'Competitors experiencing $2-5M ransom demands with 30-day operational shutdowns',
        percentage: 73
      },
      {
        category: 'Supply Chain Vulnerabilities',
        technical: 'Third-party software dependencies contain critical security flaws',
        business: '40% of companies like ours were compromised through vendor access in past 6 months',
        percentage: 40
      },
      {
        category: 'Regulatory Compliance Changes',
        technical: 'New cybersecurity framework requirements mandate additional controls',
        business: 'Non-compliance penalties starting at $100K per incident, 48-hour breach notification',
        percentage: 48
      }
    ],
    
    qaPreviewData: [
      {
        question: "How much could a major cyber incident cost our company?",
        answer: "Based on our risk assessment, a major breach could cost $2.4M-$5.4M including business disruption, legal fees, regulatory fines, and reputation damage. This represents 3-6% of our annual revenue.",
        keyStats: ["$4.88M average breach cost", "287 days to identify and contain", "38% of attacks target companies our size"],
        category: "Financial Impact"
      },
      {
        question: "Are we better or worse than our competitors at cybersecurity?",
        answer: "We're performing above industry average in most areas - blocking 98% of intrusions vs 87% industry standard. However, we have gaps in MFA adoption (94% vs 99% target) and training completion that need immediate attention.",
        keyStats: ["98% vs 87% intrusion blocking", "11% above industry average", "4 critical gaps identified"],
        category: "Competitive Position"
      },
      {
        question: "What's our biggest cybersecurity risk right now?",
        answer: "Supply chain vulnerabilities pose our highest risk (9/10 severity), with potential $5.4M impact. Recent attacks on companies like ours through vendor access points make this our top priority for Q4.",
        keyStats: ["9/10 risk severity", "$5.4M potential impact", "40% of peers compromised via vendors"],
        category: "Current Threats"
      }
    ]
  });

  const togglePresentationMode = useCallback(() => {
    setPresentationMode(!presentationMode);
  }, [presentationMode]);

  const cancelEdit = useCallback(() => {
    setEditingField(null);
    setEditValue('');
  }, []);

  // Calculate attack blocking rate
  const calculateAttackBlockingRate = () => {
    const totalAttacks = editableData.attackTrendsData.reduce((sum, month) => sum + month.attacks, 0);
    const totalBlocked = editableData.attackTrendsData.reduce((sum, month) => sum + month.blocked, 0);
    return totalAttacks > 0 ? Math.round((totalBlocked / totalAttacks) * 100) : 99;
  };

  // Calculate overall risk trend
  const calculateRiskTrend = () => {
    const totalRisks = editableData.riskData.length;
    const averageRisk = editableData.riskData.reduce((sum, risk) => sum + risk.current, 0) / totalRisks;
    const gapCount = editableData.riskData.filter(risk => risk.status === 'Gap').length;
    const improvingCount = editableData.riskData.filter(risk => risk.trend === 'down').length;
    const worseningCount = editableData.riskData.filter(risk => risk.trend === 'up').length;
    
    const overallTrend = improvingCount > worseningCount ? 'improving' : 'worsening';
    const riskGrade = averageRisk <= 3 ? 'A' : averageRisk <= 5 ? 'B' : averageRisk <= 7 ? 'C' : 'D';
    
    return { averageRisk, gapCount, overallTrend, riskGrade };
  };

  // Calculate overall performance vs industry
  const calculateOverallPerformance = () => {
    const overallScore = editableData.metricsData.reduce((sum, metric) => sum + metric.value, 0) / editableData.metricsData.length;
    const industryScore = editableData.metricsData.reduce((sum, metric) => sum + metric.industry, 0) / editableData.metricsData.length;
    const vsIndustry = overallScore / industryScore;
    return { overallScore, industryScore, vsIndustry };
  };

  // Get business-appropriate advice based on risk data
  const getBusinessAdvice = () => {
    const { averageRisk, gapCount } = calculateRiskTrend();
    const { vsIndustry } = calculateOverallPerformance();
    
    // Calculate risk penalty based on multiple factors
    const riskPenalty = Math.max(0, averageRisk - 5) + (gapCount * 0.5);
    const performanceBonus = Math.max(0, (vsIndustry - 1) * 2);
    const finalScore = averageRisk - performanceBonus + riskPenalty;
    
    if (finalScore >= 7) {
      return {
        tone: 'critical',
        summary: 'Critical cybersecurity risks threaten business operations.',
        recommendation: 'Immediate board attention and emergency budget allocation required.',
        confidence: 'Low board confidence due to high-risk exposure.'
      };
    } else if (finalScore >= 6) {
      return {
        tone: 'warning',
        summary: 'Mixed cybersecurity performance requires strategic attention.',
        recommendation: 'Targeted investments needed in identified gap areas.',
        confidence: 'Moderate board confidence with areas for improvement.'
      };
    } else if (finalScore <= 4 && vsIndustry > 1.1) {
      return {
        tone: 'positive',
        summary: 'Industry-leading cybersecurity performance provides competitive advantage.',
        recommendation: 'Leverage security strength for business growth and market positioning.',
        confidence: 'High board confidence enables strategic risk-taking.'
      };
    } else if (finalScore <= 5) {
      return {
        tone: 'good',
        summary: 'Solid cybersecurity posture supports business objectives.',
        recommendation: 'Maintain current security investments with targeted improvements.',
        confidence: 'Good board confidence in security management.'
      };
    } else {
      return {
        tone: 'neutral',
        summary: 'Balanced cybersecurity approach with mixed results.',
        recommendation: 'Continue monitoring and adjust strategy based on emerging risks.',
        confidence: 'Neutral board confidence requires ongoing attention.'
      };
    }
  };

  // Business translation helper
  const getBusinessTranslation = (technicalTerm, businessTerm) => {
    return viewMode === 'executive' ? businessTerm : technicalTerm;
  };

  // Business KPI calculations (auto-calculated from editable data)
  const calculateBusinessKPIs = () => {
    const totalInvestment = 1000000; // Base investment for calculation
    const totalAttacks = editableData.attackTrendsData.reduce((sum, month) => sum + month.attacks, 0);
    const totalBlocked = editableData.attackTrendsData.reduce((sum, month) => sum + month.blocked, 0);
    
    // ROI calculation based ONLY on incidents prevented by blocking
    // Conservative estimate: what percentage of blocked attacks would have become incidents
    const preventedMajorIncidents = Math.round(totalBlocked * 0.001); // 0.1% of blocked would have been major breaches
    const preventedMinorIncidents = Math.round(totalBlocked * 0.005); // 0.5% of blocked would have been minor incidents
    
    // Industry-standard incident costs
    const majorIncidentCost = 4880000; // Based on IBM $4.88M average breach cost
    const minorIncidentCost = 75000;   // Smaller incidents average $75K
    
    // Cost avoidance = only what we prevented, not what happened
    const costAvoidance = (preventedMajorIncidents * majorIncidentCost) + (preventedMinorIncidents * minorIncidentCost);
    const securityROI = totalInvestment > 0 ? Math.round((costAvoidance / totalInvestment) * 100) : 0;

    const patchCompliance = editableData.detectionResponseData.patchCompliance;
    const mfaAdoption = editableData.metricsData.find(m => m.metric === 'MFA Adoption')?.value || 94;
    const trainingCompletion = editableData.metricsData.find(m => m.metric === 'Training Completion')?.value || 89;
    const regulatoryCompliance = Math.round((patchCompliance + mfaAdoption + trainingCompletion) / 3);

    // Calculate Business Continuity based on attack success rate and security posture
    const attackSuccessRate = totalAttacks > 0 ? ((totalAttacks - totalBlocked) / totalAttacks) * 100 : 0;
    const avgRiskLevel = editableData.riskData.reduce((sum, r) => sum + r.current, 0) / editableData.riskData.length;
    const businessContinuity = Math.max(95.0, Math.min(99.9, 
      99.9 - (attackSuccessRate * 5) - (avgRiskLevel * 0.2)
    ));

    // Calculate Cyber Insurance Discount based on overall security performance
    const avgSecurityMetrics = editableData.metricsData.reduce((sum, m) => sum + m.value, 0) / editableData.metricsData.length;
    const riskScore = 10 - avgRiskLevel; // Inverted risk (higher is better)
    const insuranceDiscount = Math.max(0, Math.min(35, 
      (avgSecurityMetrics / 100 * 20) + (riskScore * 2) + (regulatoryCompliance / 100 * 15) - 15
    ));

    return [
      {
        metric: 'Security ROI',
        value: securityROI,
        target: 200,
        industry: 180,
        unit: '%',
        businessMeaning: 'Return on security investment - money saved vs money spent',
        impact: `We avoided ${(costAvoidance / 1000000).toFixed(1)}M in potential losses while investing ${(totalInvestment / 1000000).toFixed(1)}M in security (${totalBlocked.toLocaleString()} attacks blocked)`
      },
      {
        metric: 'Business Continuity',
        value: Math.round(businessContinuity * 10) / 10, // Round to 1 decimal
        target: 99.5,
        industry: 98.2,
        unit: '%',
        businessMeaning: 'Percentage of time critical business systems stayed operational without security disruption',
        impact: `Systems available ${businessContinuity.toFixed(1)}% of time with ${attackSuccessRate.toFixed(1)}% attack success rate and ${avgRiskLevel.toFixed(1)}/10 average risk level`
      },
      {
        metric: 'Regulatory Compliance',
        value: regulatoryCompliance,
        target: 95,
        industry: 87,
        unit: '%',
        businessMeaning: 'How well we meet legal and regulatory security requirements',
        impact: `Average of patch compliance (${patchCompliance}%), MFA adoption (${mfaAdoption}%), and training completion (${trainingCompletion}%)`
      },
      {
        metric: 'Cyber Insurance Discount',
        value: Math.round(insuranceDiscount),
        target: 15,
        industry: 8,
        unit: '%',
        businessMeaning: 'Insurance premium reduction due to strong security posture',
        impact: `${Math.round(insuranceDiscount)}% discount based on ${avgSecurityMetrics.toFixed(0)}% avg security metrics, ${riskScore.toFixed(1)}/10 risk score, and ${regulatoryCompliance}% compliance`
      }
    ];
  };

  // Data management functions
  const exportToCSV = () => {
    const csvData = [];
    
    // Add company data
    Object.entries(companyData).forEach(([key, value]) => {
      csvData.push({
        category: 'company',
        subcategory: '',
        field: key,
        value: value,
        index: 0
      });
    });
    
    // Add risk data
    editableData.riskData.forEach((risk, index) => {
      Object.entries(risk).forEach(([key, value]) => {
        csvData.push({
          category: 'risk',
          subcategory: risk.category || '',
          field: key,
          value: value,
          index: index
        });
      });
    });
    
    // Add metrics data
    editableData.metricsData.forEach((metric, index) => {
      Object.entries(metric).forEach(([key, value]) => {
        csvData.push({
          category: 'metrics',
          subcategory: metric.metric || '',
          field: key,
          value: value,
          index: index
        });
      });
    });
    
    // Add attack trends data
    editableData.attackTrendsData.forEach((attack, index) => {
      Object.entries(attack).forEach(([key, value]) => {
        csvData.push({
          category: 'attackTrends',
          subcategory: attack.month || '',
          field: key,
          value: value,
          index: index
        });
      });
    });
    
    // Add detection response data
    Object.entries(editableData.detectionResponseData).forEach(([key, value]) => {
      csvData.push({
        category: 'detectionResponse',
        subcategory: '',
        field: key,
        value: value,
        index: 0
      });
    });
    
    // Add threat intelligence data
    editableData.threatIntelligence.forEach((threat, index) => {
      Object.entries(threat).forEach(([key, value]) => {
        csvData.push({
          category: 'threatIntelligence',
          subcategory: threat.category || '',
          field: key,
          value: Array.isArray(value) ? value.join('|') : value,
          index: index
        });
      });
    });
    
    // Add Q&A data
    editableData.qaPreviewData.forEach((qa, index) => {
      Object.entries(qa).forEach(([key, value]) => {
        csvData.push({
          category: 'qaPreview',
          subcategory: qa.question || '',
          field: key,
          value: Array.isArray(value) ? value.join('|') : value,
          index: index
        });
      });
    });
    
    // Convert to CSV string
    const headers = ['category', 'subcategory', 'field', 'value', 'index'];
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => 
        headers.map(header => {
          const value = row[header];
          // Escape quotes and wrap in quotes if contains comma or quotes
          const stringValue = String(value).replace(/"/g, '""');
          return stringValue.includes(',') || stringValue.includes('"') ? `"${stringValue}"` : stringValue;
        }).join(',')
      )
    ].join('\n');
    
    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `inp2-cyber-bridge-data-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const importFromCSV = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('❌ Please select a CSV file (.csv extension required)');
      event.target.value = ''; // Reset file input
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('❌ File too large. Please use a CSV file smaller than 5MB.');
      event.target.value = '';
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvText = e.target.result;
        
        // Basic format validation
        if (!csvText.includes('category,subcategory,field,value,index')) {
          alert('❌ Invalid CSV format.\n\nThis tool only accepts CSV files exported from INP² Executive Cyber Bridge.\n\nTo create a compatible file:\n1. Use the Export CSV button\n2. Edit the exported file\n3. Re-import the modified file');
          event.target.value = '';
          return;
        }
        
        const lines = csvText.split('\n').filter(line => line.trim());
        
        if (lines.length < 10) {
          alert('❌ CSV file appears incomplete or corrupted.\n\nPlease ensure you\'re using a complete export file from this tool.');
          event.target.value = '';
          return;
        }
        
        const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
        
        const rows = lines.slice(1).map(line => {
          const values = [];
          let current = '';
          let inQuotes = false;
          
          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            if (char === '"') {
              inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
              values.push(current.trim().replace(/"/g, ''));
              current = '';
            } else {
              current += char;
            }
          }
          values.push(current.trim().replace(/"/g, ''));
          
          const row = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || '';
          });
          return row;
        });
        
        // Validate required categories exist
        const categories = [...new Set(rows.map(row => row.category))];
        const requiredCategories = ['company', 'risk', 'metrics', 'attackTrends', 'detectionResponse'];
        const missingCategories = requiredCategories.filter(cat => !categories.includes(cat));
        
        if (missingCategories.length > 0) {
          alert(`❌ CSV file is missing required data categories: ${missingCategories.join(', ')}\n\nPlease use a complete export file from this tool.`);
          event.target.value = '';
          return;
        }
        
        const newCompanyData = { ...companyData };
        const newEditableData = {
          riskData: [],
          metricsData: [],
          attackTrendsData: [],
          detectionResponseData: { ...editableData.detectionResponseData },
          threatIntelligence: [],
          qaPreviewData: []
        };
        
        // Group data by category and index
        const groupedData = {};
        rows.forEach(row => {
          const { category, field, value, index } = row;
          if (!groupedData[category]) groupedData[category] = {};
          if (!groupedData[category][index]) groupedData[category][index] = {};
          
          // Convert numeric values and handle arrays
          let convertedValue = value;
          if (!isNaN(value) && value !== '') {
            convertedValue = value.includes('.') ? parseFloat(value) : parseInt(value);
          } else if (field === 'keyStats' && value.includes('|')) {
            // Handle array fields that were joined with |
            convertedValue = value.split('|');
          }
          
          groupedData[category][index][field] = convertedValue;
        });
        
        // Reconstruct company data
        if (groupedData.company && groupedData.company[0]) {
          Object.assign(newCompanyData, groupedData.company[0]);
        }
        
        // Reconstruct other data arrays
        ['risk', 'metrics', 'attackTrends', 'threatIntelligence', 'qaPreview'].forEach(category => {
          if (groupedData[category]) {
            const categoryKey = category === 'risk' ? 'riskData' : 
                              category === 'metrics' ? 'metricsData' :
                              category === 'attackTrends' ? 'attackTrendsData' :
                              category === 'qaPreview' ? 'qaPreviewData' :
                              'threatIntelligence';
            
            newEditableData[categoryKey] = Object.values(groupedData[category]);
          }
        });
        
        // Reconstruct detection response data
        if (groupedData.detectionResponse && groupedData.detectionResponse[0]) {
          Object.assign(newEditableData.detectionResponseData, groupedData.detectionResponse[0]);
        }
        
        // Final validation check
        if (newEditableData.riskData.length < 3 || newEditableData.metricsData.length < 3) {
          alert('❌ Imported data appears incomplete.\n\nRisk data or metrics data is missing. Please check your CSV file and try again.');
          event.target.value = '';
          return;
        }
        
        setCompanyData(newCompanyData);
        setEditableData(newEditableData);
        
        // Success message with details
        alert(`✅ Data imported successfully!\n\n📊 Imported:\n• Company: ${newCompanyData.companyName}\n• ${newEditableData.riskData.length} risk categories\n• ${newEditableData.metricsData.length} security metrics\n• ${newEditableData.attackTrendsData.length} months of attack data\n\nYour customized data is now loaded and all calculations have been updated.`);
        
      } catch (error) {
        console.error('Import failed:', error);
        alert(`❌ Import failed due to file format error.\n\nError details: ${error.message}\n\nPlease ensure you're using a CSV file exported from INP² Executive Cyber Bridge.`);
        event.target.value = '';
      }
    };
    
    reader.onerror = () => {
      alert('❌ Failed to read the file.\n\nPlease try again or contact support if the problem persists.');
      event.target.value = '';
    };
    
    reader.readAsText(file);
  };

  const saveEdit = () => {
    if (!editingField) return;
    
    const fieldParts = editingField.split('-');
    const dataType = fieldParts[0];
    
    if (dataType === 'detection') {
      // Detection data doesn't have an index, just dataType-property
      const property = fieldParts[1];
      const newDetectionData = { ...editableData.detectionResponseData };
      if (['meanTimeToDetect', 'intrusionsBlocked', 'meanTimeToRespond', 'patchCompliance', 'industryMTTD', 'industryIntrusions', 'targetMTTR', 'targetPatching'].includes(property)) {
        newDetectionData[property] = parseInt(editValue) || 0;
      } else {
        newDetectionData[property] = editValue;
      }
      setEditableData(prev => ({ ...prev, detectionResponseData: newDetectionData }));
    } else {
      // All other data types use dataType-index-property format
      const numIndex = parseInt(fieldParts[1]);
      const property = fieldParts[2];
      
      if (dataType === 'risk') {
        const newRiskData = editableData.riskData.map((risk, idx) => {
          if (idx === numIndex) {
            const updatedRisk = { ...risk };
            if (property === 'current' || property === 'previous') {
              updatedRisk[property] = parseInt(editValue) || 0;
              // Auto-calculate trend
              updatedRisk.trend = updatedRisk.current < updatedRisk.previous ? 'down' : 
                                 updatedRisk.current > updatedRisk.previous ? 'up' : 'stable';
            } else if (property === 'financialRisk') {
              updatedRisk[property] = parseFloat(editValue) * 1000000 || 0;
            } else {
              updatedRisk[property] = editValue;
            }
            return updatedRisk;
          }
          return risk;
        });
        setEditableData(prev => ({ ...prev, riskData: newRiskData }));
      } else if (dataType === 'metric') {
        const newMetricsData = editableData.metricsData.map((metric, idx) => {
          if (idx === numIndex) {
            const updatedMetric = { ...metric };
            if (property === 'value' || property === 'target' || property === 'industry') {
              updatedMetric[property] = parseInt(editValue) || 0;
            } else {
              updatedMetric[property] = editValue;
            }
            return updatedMetric;
          }
          return metric;
        });
        setEditableData(prev => ({ ...prev, metricsData: newMetricsData }));
      } else if (dataType === 'attack') {
        const newAttackData = editableData.attackTrendsData.map((attack, idx) => {
          if (idx === numIndex) {
            const updatedAttack = { ...attack };
            if (property === 'attacks' || property === 'blocked') {
              updatedAttack[property] = parseInt(editValue) || 0;
            } else {
              updatedAttack[property] = editValue;
            }
            return updatedAttack;
          }
          return attack;
        });
        setEditableData(prev => ({ ...prev, attackTrendsData: newAttackData }));
      } else if (dataType === 'threat') {
        const newThreatData = editableData.threatIntelligence.map((threat, idx) => {
          if (idx === numIndex) {
            const updatedThreat = { ...threat };
            if (property === 'percentage') {
              updatedThreat[property] = parseInt(editValue) || 0;
            } else {
              updatedThreat[property] = editValue;
            }
            return updatedThreat;
          }
          return threat;
        });
        setEditableData(prev => ({ ...prev, threatIntelligence: newThreatData }));
      } else if (dataType === 'qa') {
        const newQAData = editableData.qaPreviewData.map((qa, idx) => {
          if (idx === numIndex) {
            const updatedQA = { ...qa };
            if (property === 'keyStats') {
              // Handle keyStats as comma-separated string
              updatedQA[property] = editValue.split(',').map(s => s.trim());
            } else {
              updatedQA[property] = editValue;
            }
            return updatedQA;
          }
          return qa;
        });
        setEditableData(prev => ({ ...prev, qaPreviewData: newQAData }));
      } else if (dataType === 'company') {
        if (property === 'revenue') {
          const cleanValue = editValue.replace(/[$M,]/g, '');
          setCompanyData(prev => ({ ...prev, [property]: parseFloat(cleanValue) * 1000000 || 0 }));
        } else if (property === 'employees') {
          setCompanyData(prev => ({ ...prev, [property]: parseInt(editValue) || 0 }));
        } else {
          setCompanyData(prev => ({ ...prev, [property]: editValue }));
        }
      }
    }
    
    setEditingField(null);
    setEditValue('');
  };

  const startEdit = (fieldId, currentValue) => {
    setEditingField(fieldId);
    setEditValue(String(currentValue));
  };

  const handleEditKeyPress = (e) => {
    if (e.key === 'Enter') {
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  const printSlide = (slideIndex = null) => {
    const printWindow = window.open('', '_blank');
    const currentData = slideIndex !== null ? [slides[slideIndex]] : slides;
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>INP² Executive Cyber Bridge Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
            .header { text-align: center; margin-bottom: 40px; border-bottom: 2px solid #3B82F6; padding-bottom: 20px; }
            .slide { page-break-after: always; margin-bottom: 60px; }
            .slide:last-child { page-break-after: avoid; }
            .slide-title { color: #1F2937; font-size: 24px; font-weight: bold; margin-bottom: 10px; }
            .slide-subtitle { color: #6B7280; font-size: 16px; margin-bottom: 30px; }
            .content { color: #374151; }
            .risk-box { border: 1px solid #D1D5DB; padding: 15px; margin: 10px 0; border-radius: 8px; }
            .metric-value { font-size: 28px; font-weight: bold; margin: 10px 0; }
            .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #E5E7EB; font-size: 12px; color: #6B7280; }
            @media print {
              body { margin: 20px; }
              .slide { page-break-after: always; }
              .slide:last-child { page-break-after: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>INP² Executive Cyber Bridge</h1>
            <p>Cybersecurity Board Report - ${companyData.companyName}</p>
            <p>Generated: ${new Date().toLocaleDateString()}</p>
          </div>
          ${currentData.map((slide, index) => `
            <div class="slide">
              <h2 class="slide-title">${slide.title}</h2>
              <p class="slide-subtitle">${slide.subtitle}</p>
              <div class="content">
                <!-- Simplified content for print -->
                <p>Executive Summary: ${getBusinessAdvice().summary}</p>
                <p>Recommendation: ${getBusinessAdvice().recommendation}</p>
                <p>Board Confidence: ${getBusinessAdvice().confidence}</p>
              </div>
            </div>
          `).join('')}
          <div class="footer">
            <p>© 2025 INP² - Executive Cybersecurity Assessment Tool</p>
            <p>This report contains confidential business information and should be handled according to company data classification policies.</p>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Component helpers
const InfoTooltip = ({ content, children }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    
    const handleMouseEnter = (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      setPosition({
        x: rect.left + rect.width / 2,
        y: rect.top - 10
      });
      setIsVisible(true);
    };
    
    const handleMouseLeave = () => {
      setIsVisible(false);
    };
    
    return (
      <>
        <span 
          className="cursor-help inline-block"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {children}
        </span>
        {isVisible && (
          <div 
            className="fixed pointer-events-none z-[99999]"
            style={{
              left: position.x,
              top: position.y,
              transform: 'translate(-50%, -100%)',
            }}
          >
            <div className="bg-white border-2 border-gray-300 rounded-lg shadow-xl px-4 py-3 max-w-sm">
              <div className="text-gray-800 text-sm leading-relaxed font-medium">
                {content}
              </div>
              <div 
                className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0"
                style={{
                  borderLeft: '10px solid transparent',
                  borderRight: '10px solid transparent', 
                  borderTop: '10px solid white',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                }}
              />
            </div>
          </div>
        )}
      </>
    );
  };
  const EditableField = ({ fieldId, value, type = 'text', className = '' }) => {
    if (viewMode !== 'edit') {
      return <span className={className}>{value}</span>;
    }

    if (editingField === fieldId) {
      return (
        <div className="inline-flex items-center gap-1">
          <input
            type={type}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyPress={handleEditKeyPress}
            onBlur={saveEdit}
            className="bg-yellow-100 border border-yellow-400 rounded px-1 text-sm min-w-0"
            style={{ width: `${Math.max(4, editValue.length + 2)}ch` }}
            autoFocus
          />
          <button onClick={saveEdit} className="text-green-600 hover:text-green-800 text-sm">✓</button>
        </div>
      );
    }

    return (
      <span 
        className={`${className} cursor-pointer hover:bg-yellow-100 px-1 rounded border border-dashed border-transparent hover:border-yellow-400`}
        onClick={() => startEdit(fieldId, value)}
        title="Click to edit"
      >
        {value}
      </span>
    );
  };

  const MetricDisplay = ({ metric, index }) => {
    const getStatusColor = (value, target, industry) => {
      if (value >= target) return 'text-green-600';
      if (value >= industry) return 'text-blue-600';
      return 'text-orange-600';
    };

    const getStatusIcon = (value, target, industry) => {
      if (value >= target) return <CheckCircle className="w-4 h-4 text-green-600" />;
      if (value >= industry) return <Clock className="w-4 h-4 text-blue-600" />;
      return <AlertTriangle className="w-4 h-4 text-orange-600" />;
    };

    const getStatusText = (value, target, industry) => {
      if (value >= target) return 'Above Target';
      if (value >= industry) return 'Above Industry';
      return 'Below Target';
    };

    return (
      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
        <div className="flex-1">
          <div className="font-medium text-gray-900">
            <EditableField fieldId={`metric-${index}-metric`} value={metric.metric} />
          </div>
          <div className="text-sm text-gray-600">
            Target: <EditableField fieldId={`metric-${index}-target`} value={metric.target} type="number" />
            {metric.unit} | Industry: <EditableField fieldId={`metric-${index}-industry`} value={metric.industry} type="number" />
            {metric.unit}
          </div>
        </div>
        <div className="text-right">
          <div className={`text-2xl font-bold ${getStatusColor(metric.value, metric.target, metric.industry)}`}>
            <EditableField fieldId={`metric-${index}-value`} value={metric.value} type="number" className={`text-2xl font-bold ${getStatusColor(metric.value, metric.target, metric.industry)}`} />
            {metric.unit}
          </div>
          <div className="flex items-center gap-1 text-sm">
            {getStatusIcon(metric.value, metric.target, metric.industry)}
            {getStatusText(metric.value, metric.target, metric.industry)}
          </div>
        </div>
      </div>
    );
  };

  const BusinessKPIDisplay = ({ kpi }) => {
    const getKPIColor = (value, target, industry) => {
      if (value >= target) return 'text-green-600';
      if (value >= industry) return 'text-blue-600';
      return 'text-orange-600';
    };

    const getKPIBackground = (value, target, industry) => {
      if (value >= target) return 'bg-green-50 border-green-200';
      if (value >= industry) return 'bg-blue-50 border-blue-200';
      return 'bg-orange-50 border-orange-200';
    };

    return (
      <div className={`p-4 rounded-lg border ${getKPIBackground(kpi.value, kpi.target, kpi.industry)}`}>
        <div className="text-center mb-3">
          <div className={`text-3xl font-bold ${getKPIColor(kpi.value, kpi.target, kpi.industry)}`}>
            {kpi.value}{kpi.unit}
          </div>
          <div className="text-sm text-gray-600">Target: {kpi.target}{kpi.unit}</div>
        </div>
        <div className="text-sm">
          <div className="font-medium text-gray-900 mb-1">{kpi.metric}</div>
          <div className="text-gray-700 mb-2">{kpi.businessMeaning}</div>
          <div className="text-gray-600 text-xs">{kpi.impact}</div>
          <div className="flex justify-between items-center mt-2">
            <span className={`text-xs px-2 py-1 rounded ${kpi.value >= kpi.target ? 'bg-green-100 text-green-800' : kpi.value >= kpi.industry ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'}`}>
              {kpi.value >= kpi.target ? '✓ Above Target' : kpi.value >= kpi.industry ? '↗ Above Industry' : '⚠ Below Target'}
            </span>
            <span className="text-xs text-gray-500">Industry: {kpi.industry}{kpi.unit}</span>
          </div>
        </div>
      </div>
    );
  };

  const QAPreview = ({ qaData, viewMode, EditableField }) => {
    return (
      <div className="space-y-4">
        {qaData.slice(0, 3).map((qa, index) => (
          <div key={index} className="border rounded-lg p-4 bg-white">
            <div className="flex items-start gap-3">
              <div className="bg-blue-100 p-2 rounded-full">
                <HelpCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h5 className="font-semibold text-gray-900 mb-2">
                  <EditableField fieldId={`qa-${index}-question`} value={qa.question} className="font-semibold" />
                </h5>
                <p className="text-sm text-gray-700 mb-3">
                  <EditableField fieldId={`qa-${index}-answer`} value={qa.answer} />
                </p>
                <div className="bg-gray-50 p-2 rounded">
                  <div className="text-xs text-gray-600 mb-1">Key Statistics:</div>
                  <div className="flex flex-wrap gap-2">
                    {qa.keyStats.map((stat, statIndex) => (
                      <span key={statIndex} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                        {stat}
                      </span>
                    ))}
                  </div>
                  {viewMode === 'edit' && (
                    <div className="mt-2">
                      <label className="text-xs text-gray-600">Edit stats (comma-separated):</label>
                      <EditableField fieldId={`qa-${index}-keyStats`} value={qa.keyStats.join(', ')} className="text-xs" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const ModeSelector = () => {
    const modes = [
      { id: 'executive', label: 'Executive', icon: Briefcase, description: 'Business-focused language' },
      { id: 'technical', label: 'Technical', icon: Shield, description: 'Technical terminology' },
      { id: 'edit', label: 'Edit', icon: Users, description: 'Customize data' }
    ];

    return (
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
        {modes.map((mode) => {
          const Icon = mode.icon;
          return (
            <button
              key={mode.id}
              onClick={() => setViewMode(mode.id)}
              className={`px-3 py-1 rounded text-sm flex items-center gap-1 transition-colors ${
                viewMode === mode.id
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-200'
              }`}
              title={mode.description}
            >
              <Icon className="w-4 h-4" />
              {mode.label}
            </button>
          );
        })}
      </div>
    );
  };

  // Define slides with dynamic content that updates based on current data
  const slides = [
    {
      title: "Why Cybersecurity Matters to Our Business",
      subtitle: "Cyber Risk = Business Risk",
      content: (
        <div className="space-y-6">
          <div className={`p-4 rounded-lg border-l-4 ${
            getBusinessAdvice().tone === 'positive' ? 'bg-green-50 border-green-500' : 
            getBusinessAdvice().tone === 'good' ? 'bg-blue-50 border-blue-500' : 
            getBusinessAdvice().tone === 'warning' ? 'bg-yellow-50 border-yellow-500' :
            getBusinessAdvice().tone === 'critical' ? 'bg-red-50 border-red-500' :
            'bg-gray-50 border-gray-500'
          }`}>
            <h4 className={`font-semibold mb-2 ${
              getBusinessAdvice().tone === 'positive' ? 'text-green-800' : 
              getBusinessAdvice().tone === 'good' ? 'text-blue-800' : 
              getBusinessAdvice().tone === 'warning' ? 'text-yellow-800' :
              getBusinessAdvice().tone === 'critical' ? 'text-red-800' :
              'text-gray-800'
            }`}>
              Executive Summary
            </h4>
            <p className={`text-sm ${
              getBusinessAdvice().tone === 'positive' ? 'text-green-700' : 
              getBusinessAdvice().tone === 'good' ? 'text-blue-700' : 
              getBusinessAdvice().tone === 'warning' ? 'text-yellow-700' :
              getBusinessAdvice().tone === 'critical' ? 'text-red-700' :
              'text-gray-700'
            }`}>
              {getBusinessAdvice().summary} Average risk level {calculateRiskTrend().averageRisk.toFixed(1)}/10 with {calculateRiskTrend().gapCount} high-impact vulnerabilities requiring immediate attention.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-red-50 p-6 rounded-lg border border-red-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-red-100 p-3 rounded-lg">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    <InfoTooltip content="Regulatory compliance failures can result in significant fines, legal liability for board members, and loss of business licenses.">
                      Regulatory Scrutiny 
                    </InfoTooltip>
                  </h3>
                </div>
              </div>
              <p className="text-gray-700 text-sm">
                Personal liability for board members when cyber incidents occur due to inadequate oversight
              </p>
            </div>

            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <DollarSign className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    <InfoTooltip content="Beyond direct costs, cyber incidents cause lasting damage to stock price, customer trust, and market position that can take years to recover.">
                      Financial Impact 
                    </InfoTooltip>
                  </h3>
                </div>
              </div>
              <p className="text-gray-700 text-sm">
                Average 21% stock price drop in 6 months following a major breach announcement
              </p>
            </div>

            <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-purple-100 p-3 rounded-lg">
                  <Shield className="w-8 h-8 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    <InfoTooltip content="Legal requirement to demonstrate cybersecurity oversight or face personal liability and reputational damage.">
                      Fiduciary Duty 
                    </InfoTooltip>
                  </h3>
                </div>
              </div>
              <p className="text-gray-700 text-sm">
                Legal requirement to demonstrate cybersecurity oversight or face personal liability
              </p>
            </div>
          </div>

          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Business Reality Check</h3>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-4xl font-bold text-red-600 mb-2">$4.88M</div>
                <div className="text-lg font-semibold text-gray-900">Average Cost</div>
                <div className="text-sm text-gray-600">What our competitors paid for their last breach (not including lost customers)</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-orange-600 mb-2">73%</div>
                <div className="text-lg font-semibold text-gray-900">Market Reality</div>
                <div className="text-sm text-gray-600">of companies like ours were forced to shut down operations temporarily due to cyber attacks</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-green-600 mb-2">${calculateBusinessKPIs()[0].value}%</div>
                <div className="text-lg font-semibold text-gray-900">Our Security ROI</div>
                <div className="text-sm text-gray-600">Return on investment from our cybersecurity spending - money saved vs money invested</div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Current Risk Posture",
      subtitle: `Overall Rating: ${calculateRiskTrend().riskGrade} (${calculateRiskTrend().overallTrend === 'improving' ? '↓ improving' : '↑ worsening'} from last quarter)`,
      content: (
        <div className="space-y-6">
          {viewMode === 'executive' && (
            <div className={`p-4 rounded-lg border-l-4 ${
              getBusinessAdvice().tone === 'positive' ? 'bg-green-50 border-green-500' : 
              getBusinessAdvice().tone === 'good' ? 'bg-blue-50 border-blue-500' : 
              getBusinessAdvice().tone === 'warning' ? 'bg-yellow-50 border-yellow-500' :
              getBusinessAdvice().tone === 'critical' ? 'bg-red-50 border-red-500' :
              'bg-gray-50 border-gray-500'
            }`}>
              <h4 className={`font-semibold mb-2 ${
                getBusinessAdvice().tone === 'positive' ? 'text-green-800' : 
                getBusinessAdvice().tone === 'good' ? 'text-blue-800' : 
                getBusinessAdvice().tone === 'warning' ? 'text-yellow-800' :
                getBusinessAdvice().tone === 'critical' ? 'text-red-800' :
                'text-gray-800'
              }`}>
                Business Impact Assessment
              </h4>
              <p className={`text-sm ${
                getBusinessAdvice().tone === 'positive' ? 'text-green-700' : 
                getBusinessAdvice().tone === 'good' ? 'text-blue-700' : 
                getBusinessAdvice().tone === 'warning' ? 'text-yellow-700' :
                getBusinessAdvice().tone === 'critical' ? 'text-red-700' :
                'text-gray-700'
              }`}>
                {getBusinessAdvice().recommendation} {getBusinessAdvice().confidence}
              </p>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-gray-900">
                {getBusinessTranslation('Risk Assessment', 'What Could Go Wrong?')}
              </h4>
              {editableData.riskData.map((risk, index) => (
                <div key={index} className={`p-4 rounded-lg border-l-4 ${
                  risk.status === 'Gap' ? 'bg-red-50 border-red-500' : 
                  risk.status === 'Improving' ? 'bg-yellow-50 border-yellow-500' : 
                  'bg-green-50 border-green-500'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-semibold text-gray-900">
                      <EditableField fieldId={`risk-${index}-category`} value={risk.category} className="font-semibold" />
                    </h5>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        risk.status === 'Gap' ? 'bg-red-100 text-red-800' : 
                        risk.status === 'Improving' ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-green-100 text-green-800'
                      }`}>
                        <EditableField fieldId={`risk-${index}-status`} value={risk.status} />
                      </span>
                      <div className="flex items-center gap-1">
                        {risk.trend === 'up' && <TrendingUp className="w-4 h-4 text-red-500" />}
                        {risk.trend === 'down' && <TrendingDown className="w-4 h-4 text-green-500" />}
                        {risk.trend === 'stable' && <Minus className="w-4 h-4 text-gray-500" />}
                        <span className="text-sm font-bold">
                          <EditableField fieldId={`risk-${index}-current`} value={risk.current} type="number" className="text-sm font-bold" />/10
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">
                    <EditableField fieldId={`risk-${index}-description`} value={risk.description} />
                  </p>
                  <div className="text-xs text-gray-600">
                    {viewMode === 'executive' ? `Potential business impact: $${(risk.financialRisk / 1000000).toFixed(1)}M` : 
                     `Financial risk: $${(risk.financialRisk / 1000000).toFixed(1)}M`}
                    {viewMode === 'edit' && (
                      <span> (Edit: <EditableField fieldId={`risk-${index}-financialRisk`} value={risk.financialRisk / 1000000} type="number" />M)</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg border shadow-sm">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">
                  {getBusinessTranslation('Risk Summary', 'Bottom Line Assessment')}
                </h4>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Overall Risk Grade</span>
                    <span className={`text-2xl font-bold px-3 py-1 rounded ${
                      calculateRiskTrend().riskGrade === 'A' ? 'bg-green-100 text-green-800' :
                      calculateRiskTrend().riskGrade === 'B' ? 'bg-blue-100 text-blue-800' :
                      calculateRiskTrend().riskGrade === 'C' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {calculateRiskTrend().riskGrade}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">High Priority Issues</span>
                    <span className="text-xl font-semibold">{calculateRiskTrend().gapCount}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Trend Direction</span>
                    <div className="flex items-center gap-1">
                      {calculateRiskTrend().overallTrend === 'improving' ? (
                        <TrendingDown className="w-5 h-5 text-green-500" />
                      ) : (
                        <TrendingUp className="w-5 h-5 text-red-500" />
                      )}
                      <span className={calculateRiskTrend().overallTrend === 'improving' ? 'text-green-600' : 'text-red-600'}>
                        {calculateRiskTrend().overallTrend === 'improving' ? 'Improving' : 'Worsening'}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">
                      {getBusinessTranslation('Average Risk Score', 'Business Risk Level')}
                    </span>
                    <span className="text-xl font-semibold">{calculateRiskTrend().averageRisk.toFixed(1)}/10</span>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border shadow-sm">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">
                  {getBusinessTranslation('Financial Exposure', 'What This Costs Us')}
                </h4>
                <div className="text-center">
                  <div className="text-3xl font-bold text-red-600 mb-2">
                    ${(editableData.riskData.reduce((sum, risk) => sum + risk.financialRisk, 0) / 1000000).toFixed(1)}M
                  </div>
                  <div className="text-sm text-gray-600">Total potential business impact from current risk exposure</div>
                  <div className="mt-4 text-xs text-gray-500">
                    This represents {((editableData.riskData.reduce((sum, risk) => sum + risk.financialRisk, 0) / companyData.revenue) * 100).toFixed(1)}% of annual revenue
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Threat Landscape Context",
      subtitle: `Are we winning the cyber war? We're blocking ${calculateAttackBlockingRate()}% of attacks - industry-leading performance`,
      content: (
        <div className="space-y-6">
          {viewMode === 'executive' && (
            <div className={`p-4 rounded-lg border-l-4 ${
              getBusinessAdvice().tone === 'positive' ? 'bg-green-50 border-green-500' : 
              getBusinessAdvice().tone === 'good' ? 'bg-blue-50 border-blue-500' : 
              getBusinessAdvice().tone === 'warning' ? 'bg-yellow-50 border-yellow-500' :
              getBusinessAdvice().tone === 'critical' ? 'bg-red-50 border-red-500' :
              'bg-gray-50 border-gray-500'
            }`}>
              <h4 className={`font-semibold mb-2 ${
                getBusinessAdvice().tone === 'positive' ? 'text-green-800' : 
                getBusinessAdvice().tone === 'good' ? 'text-blue-800' : 
                getBusinessAdvice().tone === 'warning' ? 'text-yellow-800' :
                getBusinessAdvice().tone === 'critical' ? 'text-red-800' :
                'text-gray-800'
              }`}>
                {getBusinessAdvice().tone === 'positive' ? 'Threat Landscape: Competitive Advantage' : 
                getBusinessAdvice().tone === 'good' ? 'Threat Landscape: Strong Defense Position' : 
                getBusinessAdvice().tone === 'warning' ? 'Threat Landscape: Defensive Gaps Present' :
                getBusinessAdvice().tone === 'critical' ? 'Threat Landscape: Critical Vulnerabilities' :
                'Threat Landscape: Mixed Defense Posture'}
              </h4>
              <p className={`text-sm ${
                getBusinessAdvice().tone === 'positive' ? 'text-green-700' : 
                getBusinessAdvice().tone === 'good' ? 'text-blue-700' : 
                getBusinessAdvice().tone === 'warning' ? 'text-yellow-700' :
                getBusinessAdvice().tone === 'critical' ? 'text-red-700' :
                'text-gray-700'
              }`}>
                {getBusinessAdvice().tone === 'positive' ? 
                  `Our ${calculateAttackBlockingRate()}% attack blocking rate significantly exceeds industry standards (85%), creating a powerful competitive advantage and enabling premium positioning in the market.` :
                getBusinessAdvice().tone === 'good' ?
                  `Strong defensive performance with ${calculateAttackBlockingRate()}% attack blocking rate vs 85% industry average. Our security capabilities support business growth and customer confidence.` :
                getBusinessAdvice().tone === 'warning' ?
                  `Despite ${calculateAttackBlockingRate()}% attack blocking rate, underlying security gaps create business risk. Our defensive capabilities need strengthening to maintain market position.` :
                getBusinessAdvice().tone === 'critical' ?
                  `Critical security vulnerabilities undermine our defensive strategy. Even with ${calculateAttackBlockingRate()}% blocking rate, high-risk gaps threaten business continuity and competitive position.` :
                  `Mixed threat landscape performance with ${calculateAttackBlockingRate()}% attack blocking rate. Balanced investment needed to optimize both defense and business value.`
                }
              </p>
            </div>
          )}
          
          {viewMode === 'executive' ? (
            // Executive Business View
            <div className="space-y-6">
              {/* Business Performance Dashboard */}
              <div 
                className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-xl border border-green-200"
                key={`winning-war-${editableData.attackTrendsData.reduce((sum, d) => sum + d.attacks + d.blocked, 0)}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-green-600 p-3 rounded-lg">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">🎯 Are We Winning the Cyber War?</h3>
                    <p className="text-gray-600">Strategic Security Performance vs Market Reality</p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-white p-4 rounded-lg border shadow-sm">
                    <div className="text-center">
                      <div className="text-4xl font-bold text-green-600 mb-2">{calculateAttackBlockingRate()}%</div>
                      <div className="text-lg font-semibold text-gray-900">Victory Rate</div>
                      <div className="text-sm text-green-600 mb-3">We're stopping {calculateAttackBlockingRate()}% of all attacks</div>
                      <div className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                        vs Industry: 85% (We're {calculateAttackBlockingRate() - 85}% better)
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg border shadow-sm">
                    <div className="text-center">
                      <div className="text-4xl font-bold text-blue-600 mb-2">
                        ${((editableData.attackTrendsData.reduce((sum, month) => sum + (month.attacks - month.blocked), 0) * 50000) / 1000000).toFixed(1)}M
                      </div>
                      <div className="text-lg font-semibold text-gray-900">Revenue Protected</div>
                      <div className="text-sm text-blue-600 mb-3">Business losses prevented this quarter</div>
                      <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                        Each blocked attack saves ~$50K
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg border shadow-sm">
                    <div className="text-center">
                      <div className="text-4xl font-bold text-purple-600 mb-2">
                        {editableData.attackTrendsData.reduce((sum, month) => sum + month.blocked, 0)}
                      </div>
                      <div className="text-lg font-semibold text-gray-900">Attacks Defeated</div>
                      <div className="text-sm text-purple-600 mb-3">Successful defenses in 6 months</div>
                      <div className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                        Each = potential business disruption avoided
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Strategic Business Implications */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-lg border shadow-sm">
                  <InfoTooltip content="How our cybersecurity performance creates competitive business advantages and drives revenue growth.">
                    <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <DollarSign className="w-6 h-6 text-green-600" />
                      💰 Strategic Business Value
                    </h4>
                  </InfoTooltip>
                  
                  <div className="space-y-4">
                    <div className="border-l-4 border-green-500 pl-4">
                      <h5 className="font-semibold text-green-800 mb-2">✅ Revenue Protection & Growth</h5>
                      <ul className="text-sm text-green-700 space-y-1">
                        <li>• <strong>Customer Trust:</strong> Zero breaches = 100% customer retention</li>
                        <li>• <strong>Contract Wins:</strong> Security certifications won us 3 major deals ({((calculateAttackBlockingRate() - 85) * 50000).toLocaleString()} value)</li>
                        <li>• <strong>Premium Pricing:</strong> "Security-first" positioning justifies 15% higher rates</li>
                        <li>• <strong>Market Reputation:</strong> Industry-leading {calculateAttackBlockingRate()}% success rate drives referrals</li>
                      </ul>
                    </div>
                    
                    <div className="border-l-4 border-blue-500 pl-4">
                      <h5 className="font-semibold text-blue-800 mb-2">🎯 Competitive Positioning</h5>
                      <ul className="text-sm text-blue-700 space-y-1">
                        <li>• <strong>Market Differentiator:</strong> "Most secure option" vs competitors</li>
                        <li>• <strong>RFP Advantage:</strong> Security requirements favor us in 8/10 bids</li>
                        <li>• <strong>Industry Leadership:</strong> {calculateAttackBlockingRate() - 85}% above industry standard</li>
                        <li>• <strong>Risk-Conscious Clients:</strong> Finance/healthcare sectors prefer us</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg border shadow-sm">
                  <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-6 h-6 text-orange-600" />
                    📊 Market Intelligence & Competitor Risk
                  </h4>
                  
                  <div className="space-y-4">
                    {editableData.threatIntelligence.map((intel, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <div className={`w-3 h-3 rounded-full mt-1 ${index === 0 ? 'bg-red-500' : index === 1 ? 'bg-orange-500' : 'bg-blue-500'}`}></div>
                          <div className="flex-1">
                            <h5 className="font-semibold text-gray-900 mb-2">
                              <EditableField fieldId={'threat-' + index + '-category'} value={intel.category} className="font-semibold" />
                            </h5>
                            <p className="text-sm text-gray-700 mb-2">
                              <EditableField fieldId={'threat-' + index + '-business'} value={intel.business} />
                            </p>
                            <div className="bg-gray-50 p-2 rounded text-xs">
                              <strong>Business Impact:</strong> {
                                intel.category.includes('Ransomware') ? 'Competitors losing revenue to attackers - we\'re protected' :
                                intel.category.includes('Supply') ? 'Our vendor security screening prevented similar attacks' :
                                'Compliance advantage: we\'re already prepared for new regulations'
                              }
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Board Action Dashboard */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-xl border border-indigo-200">
                <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Briefcase className="w-6 h-6 text-indigo-600" />
                  🎪 Board Decisions & Strategic Opportunities
                </h4>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h5 className="font-semibold text-indigo-800 mb-3">🚀 Market Our Security Strength</h5>
                    <div className="space-y-2 text-sm">
                      <div className="bg-white p-3 rounded border">
                        <strong>Marketing Opportunity:</strong> Publicize our {calculateAttackBlockingRate()}% success rate vs 85% industry average
                      </div>
                      <div className="bg-white p-3 rounded border">
                        <strong>Sales Strategy:</strong> Lead with security in risk-conscious verticals (finance, healthcare)
                      </div>
                      <div className="bg-white p-3 rounded border">
                        <strong>PR Campaign:</strong> "Most Secure Choice" messaging for competitive differentiation
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h5 className="font-semibold text-indigo-800 mb-3">💼 Investment & Resource Decisions</h5>
                    <div className="space-y-2 text-sm">
                      <div className="bg-white p-3 rounded border">
                        <strong>Budget Recommendation:</strong> Maintain current security investment - it's working
                      </div>
                      <div className="bg-white p-3 rounded border">
                        <strong>Competitive Intel:</strong> Monitor competitor breaches for market opportunity
                      </div>
                      <div className="bg-white p-3 rounded border">
                        <strong>Strategic Partnership:</strong> Consider security-focused alliances
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 p-4 bg-indigo-100 rounded-lg">
                  <p className="text-indigo-800 font-semibold text-center">
                    🎯 <strong>Executive Summary:</strong> Our industry-leading cybersecurity is a major business asset. 
                    We should leverage this strength for competitive advantage and revenue growth, not just risk management.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            // Technical Detailed View
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg border shadow-sm">
                <InfoTooltip content="Shows how many attacks we're seeing versus how many we're successfully blocking. The gap represents our defensive effectiveness.">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Attack Trends (6 Months) - Technical Performance Data
                  </h4>
                </InfoTooltip>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart 
                      data={editableData.attackTrendsData} 
                      key={editableData.attackTrendsData.reduce((sum, d) => sum + d.attacks + d.blocked, 0)}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Area type="monotone" dataKey="attacks" stackId="1" stroke="#ef4444" fill="#ef4444" fillOpacity={0.3} name="Total Attacks" />
                      <Area type="monotone" dataKey="blocked" stackId="2" stroke="#10b981" fill="#10b981" fillOpacity={0.6} name="Successfully Blocked" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                
                {viewMode === 'edit' && (
                  <div className="mt-4 p-3 bg-gray-50 rounded border">
                    <h6 className="font-semibold text-gray-900 mb-2">Edit Attack Trends Data:</h6>
                    <div className="grid grid-cols-6 gap-2 text-xs">
                      {editableData.attackTrendsData.map((data, index) => (
                        <div key={index} className="space-y-1">
                          <div className="font-medium">{data.month}</div>
                          <div>
                            <label className="block text-gray-600">Attacks:</label>
                            <EditableField fieldId={'attack-' + index + '-attacks'} value={data.attacks} type="number" className="text-xs" />
                          </div>
                          <div>
                            <label className="block text-gray-600">Blocked:</label>
                            <EditableField fieldId={'attack-' + index + '-blocked'} value={data.blocked} type="number" className="text-xs" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="bg-white p-6 rounded-lg border shadow-sm">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">
                  Threat Intelligence - Technical Analysis
                </h4>
                <div className="space-y-4">
                  {editableData.threatIntelligence.map((intel, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-2 ${index === 0 ? 'bg-red-500' : index === 1 ? 'bg-orange-500' : 'bg-blue-500'}`}></div>
                      <div>
                        <p className="font-medium text-gray-900">
                          <EditableField fieldId={'threat-' + index + '-category'} value={intel.category} className="font-medium" />
                        </p>
                        <p className="text-sm text-gray-600">
                          <EditableField fieldId={'threat-' + index + '-technical'} value={intel.technical} />
                        </p>
                        {viewMode === 'edit' && (
                          <div className="mt-1 text-xs text-gray-500">
                            <label>Key Number: </label>
                            <EditableField fieldId={'threat-' + index + '-percentage'} value={intel.percentage} type="number" />
                            {intel.category.includes('Ransomware') && '%'}
                            {intel.category.includes('Supply') && ' companies'}
                            {intel.category.includes('Regulatory') && ' hours'}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )
    },
    {
      title: "Business Impact & Performance Metrics",
      subtitle: `Security ROI: ${calculateBusinessKPIs()[0]?.value || 240}% | Security effectiveness: ${Math.round(calculateOverallPerformance().vsIndustry * 100)}% vs industry average`,
      content: (
        <div className="space-y-6">
          {viewMode === 'executive' && (
            <div 
              className={`p-6 rounded-xl border ${
                getBusinessAdvice().tone === 'positive' ? 'bg-gradient-to-r from-green-50 to-blue-50 border-green-200' : 
                getBusinessAdvice().tone === 'good' ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200' : 
                getBusinessAdvice().tone === 'warning' ? 'bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200' :
                getBusinessAdvice().tone === 'critical' ? 'bg-gradient-to-r from-red-50 to-pink-50 border-red-200' :
                'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200'
              }`}
              key={`business-kpis-${editableData.attackTrendsData.reduce((sum, d) => sum + d.attacks + d.blocked, 0)}-${editableData.detectionResponseData.patchCompliance}`}
            >
              <h3 className={`text-xl font-bold mb-4 flex items-center gap-2 ${
                getBusinessAdvice().tone === 'positive' ? 'text-gray-900' : 
                getBusinessAdvice().tone === 'good' ? 'text-gray-900' : 
                getBusinessAdvice().tone === 'warning' ? 'text-gray-900' :
                getBusinessAdvice().tone === 'critical' ? 'text-gray-900' :
                'text-gray-900'
              }`}>
                <DollarSign className={`w-6 h-6 ${
                  getBusinessAdvice().tone === 'positive' ? 'text-green-600' : 
                  getBusinessAdvice().tone === 'good' ? 'text-blue-600' : 
                  getBusinessAdvice().tone === 'warning' ? 'text-yellow-600' :
                  getBusinessAdvice().tone === 'critical' ? 'text-red-600' :
                  'text-gray-600'
                }`} />
                {getBusinessTranslation('Business Impact Metrics', 'Bottom Line: How Security Drives Business Value')}
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {calculateBusinessKPIs().map((kpi, index) => (
                  <BusinessKPIDisplay key={index} kpi={kpi} />
                ))}
              </div>
              <div className={`mt-4 p-3 rounded-lg ${
                getBusinessAdvice().tone === 'positive' ? 'bg-green-100' : 
                getBusinessAdvice().tone === 'good' ? 'bg-blue-100' : 
                getBusinessAdvice().tone === 'warning' ? 'bg-yellow-100' :
                getBusinessAdvice().tone === 'critical' ? 'bg-red-100' :
                'bg-gray-100'
              }`}>
                <p className={`font-semibold text-sm ${
                  getBusinessAdvice().tone === 'positive' ? 'text-green-800' : 
                  getBusinessAdvice().tone === 'good' ? 'text-blue-800' : 
                  getBusinessAdvice().tone === 'warning' ? 'text-yellow-800' :
                  getBusinessAdvice().tone === 'critical' ? 'text-red-800' :
                  'text-gray-800'
                }`}>
                  🎯 <strong>Business Impact Summary:</strong> {
                    getBusinessAdvice().tone === 'positive' ? 
                      `Our security investments are delivering exceptional business value with ${calculateBusinessKPIs()[0].value}% ROI and industry-leading performance across all metrics.` :
                    getBusinessAdvice().tone === 'good' ?
                      `Security investments showing solid business returns with ${calculateBusinessKPIs()[0].value}% ROI, though some areas need attention to optimize performance.` :
                    getBusinessAdvice().tone === 'warning' ?
                      `Mixed security performance impacting business value. Current ${calculateBusinessKPIs()[0].value}% ROI is positive but underlying risks threaten future returns.` :
                    getBusinessAdvice().tone === 'critical' ?
                      `Critical security risks are undermining business value. Despite ${calculateBusinessKPIs()[0].value}% ROI, high-risk exposure threatens operations and future performance.` :
                      `Security performance shows mixed results with ${calculateBusinessKPIs()[0].value}% ROI. Balanced approach needed to optimize both risk management and business value.`
                  }
                </p>
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg border shadow-sm">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">
                {getBusinessTranslation('Security Performance', 'How Well Are We Protected?')}
              </h4>
              <div className="space-y-4">
                {editableData.metricsData.map((metric, index) => (
                  <MetricDisplay key={index} metric={metric} index={index} />
                ))}
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg border shadow-sm">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">
                {getBusinessTranslation('Detection & Response', 'How Fast Do We React?')}
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <InfoTooltip content="How quickly we spot an attack in progress. Faster = less damage.">
                    <p className="text-3xl font-bold text-green-600">
                      <EditableField fieldId="detection-meanTimeToDetect" value={editableData.detectionResponseData.meanTimeToDetect} type="number" className="text-3xl font-bold text-green-600" />hrs
                    </p>
                  </InfoTooltip>
                  <p className="text-sm text-gray-600">Mean Time to Detect</p>
                  <p className="text-xs text-gray-500">
                    (Industry: <EditableField fieldId="detection-industryMTTD" value={editableData.detectionResponseData.industryMTTD} type="number" />hrs)
                  </p>
                </div>
                <div className="text-center">
                  <InfoTooltip content="Percentage of attack attempts we successfully stop before they cause damage.">
                    <p className="text-3xl font-bold text-blue-600">
                      <EditableField fieldId="detection-intrusionsBlocked" value={editableData.detectionResponseData.intrusionsBlocked} type="number" className="text-3xl font-bold text-blue-600" />%
                    </p>
                  </InfoTooltip>
                  <p className="text-sm text-gray-600">Intrusions Blocked</p>
                  <p className="text-xs text-gray-500">
                    (Industry: <EditableField fieldId="detection-industryIntrusions" value={editableData.detectionResponseData.industryIntrusions} type="number" />%)
                  </p>
                </div>
                <div className="text-center">
                  <InfoTooltip content="How quickly we contain and stop an attack once detected.">
                    <p className="text-3xl font-bold text-purple-600">
                      <EditableField fieldId="detection-meanTimeToRespond" value={editableData.detectionResponseData.meanTimeToRespond} type="number" className="text-3xl font-bold text-purple-600" />hrs
                    </p>
                  </InfoTooltip>
                  <p className="text-sm text-gray-600">Mean Time to Respond</p>
                  <p className="text-xs text-gray-500">
                    (Target: <EditableField fieldId="detection-targetMTTR" value={editableData.detectionResponseData.targetMTTR} type="number" />hrs)
                  </p>
                </div>
                <div className="text-center">
                  <InfoTooltip content="How current we are with security updates. Higher = smaller window for attacks.">
                    <p className="text-3xl font-bold text-orange-600">
                      <EditableField fieldId="detection-patchCompliance" value={editableData.detectionResponseData.patchCompliance} type="number" className="text-3xl font-bold text-orange-600" />%
                    </p>
                  </InfoTooltip>
                  <p className="text-sm text-gray-600">Patch Compliance</p>
                  <p className="text-xs text-gray-500">
                    (Target: <EditableField fieldId="detection-targetPatching" value={editableData.detectionResponseData.targetPatching} type="number" />%)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Board Q&A Preparation Guide",
      subtitle: "Evidence-based responses to common cybersecurity questions",
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-xl border border-indigo-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-indigo-600 p-2 rounded-lg">
                <MessageSquare className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Board Q&A Preparation</h3>
            </div>
            <p className="text-gray-700 mb-4">
              Never be caught off-guard by board questions again. This comprehensive guide provides evidence-based responses 
              to the most common cybersecurity questions executives face, all backed by verified industry statistics and research.
            </p>
          </div>

          <QAPreview 
            qaData={editableData.qaPreviewData}
            viewMode={viewMode}
            EditableField={EditableField}
          />

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-800 mb-1">This is a Preview</h4>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  // Then define navigation functions that depend on slides
  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape') {
        if (showImportExportHelp) {
          setShowImportExportHelp(false);
          return;
        }
        if (editingField !== null) {
          cancelEdit();
          return;
        }
        if (presentationMode) {
          togglePresentationMode();
          return;
        }
      }
      if (editingField !== null) return; // Don't navigate while editing
      if (e.key === 'ArrowRight' && currentSlide < slides.length - 1) nextSlide();
      if (e.key === 'ArrowLeft' && currentSlide > 0) prevSlide();
      if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        togglePresentationMode();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentSlide, slides.length, presentationMode, editingField, showImportExportHelp, nextSlide, prevSlide, togglePresentationMode, cancelEdit, editableData.attackTrendsData]);

  return (
    <div className={`min-h-screen ${presentationMode ? 'bg-black' : 'bg-gradient-to-br from-slate-100 to-blue-100'}`}>
      {/* Import/Export Help Modal */}
      {showImportExportHelp && (
        <div 
          className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 flex items-center justify-center p-4"
          style={{ zIndex: 99999, position: 'fixed' }}
          onClick={(e) => {
            // Close modal if clicking the backdrop
            if (e.target === e.currentTarget) {
              setShowImportExportHelp(false);
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="sticky top-0 bg-white border-b p-6 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Import & Export Data</h3>
              <button 
                onClick={() => setShowImportExportHelp(false)}
                className="text-gray-400 hover:text-gray-600 p-1 text-xl"
              >
                ✕
              </button>
            </div>
            
            <div className="p-6">
              <div className="space-y-6">
                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Upload className="w-5 h-5 text-blue-600" />
                    <h4 className="font-semibold text-gray-900">Import CSV</h4>
                  </div>
                  <ul className="text-sm text-gray-700 space-y-1 mb-3">
                    <li>• Upload CSV files exported from this tool</li>
                    <li>• Restore saved configurations</li>
                    <li>• Load bulk-edited data</li>
                    <li>• Share templates between organizations</li>
                  </ul>
                  <div className="mt-3 p-3 bg-yellow-50 rounded text-xs text-yellow-800 border border-yellow-200">
                    <div className="font-semibold mb-2">⚠️ INP² CSV Format Required</div>
                    <div className="mb-2">Only accepts CSV files with this exact structure:</div>
                    <div className="bg-white p-2 rounded border font-mono text-xs overflow-x-auto">
                      <div className="text-gray-600">Headers:</div>
                      <div>category,subcategory,field,value,index</div>
                      <div className="text-gray-600 mt-1">Sample rows:</div>
                      <div>company,,companyName,Your Company,0</div>
                      <div>risk,Phishing,current,7,0</div>
                      <div>metrics,MFA Adoption,value,94,0</div>
                    </div>
                    <div className="mt-2 text-yellow-700">
                      <strong>To create compatible files:</strong> Use Export CSV button, then edit the exported file in Excel/Google Sheets, then re-import.
                    </div>
                  </div>
                </div>
                
                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Download className="w-5 h-5 text-green-600" />
                    <h4 className="font-semibold text-gray-900">Export CSV</h4>
                  </div>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Download all customized data in INP² format</li>
                    <li>• Edit in Excel/Google Sheets for bulk changes</li>
                    <li>• Backup configurations before major updates</li>
                    <li>• Share templates with colleagues</li>
                    <li>• Create multiple scenarios for different presentations</li>
                  </ul>
                </div>
                
                <div className="border rounded-lg p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Shield className="w-5 h-5 text-indigo-600" />
                    <h4 className="font-semibold text-indigo-900">⚡ Premium Version</h4>
                  </div>
                  <p className="text-sm text-indigo-800 mb-2">Skip manual data management entirely:</p>
                  <ul className="text-sm text-indigo-700 space-y-1">
                    <li>• <strong>Live sync</strong> from security platforms</li>
                    <li>• <strong>Auto-import</strong> from Balbix, FAIR, SOAR, Nessus</li>
                    <li>• <strong>Real-time updates</strong> for board presentations</li>
                    <li>• <strong>Zero manual entry</strong> - always current data</li>
                    <li>• <strong>Scheduled reports</strong> with fresh data automatically</li>
                  </ul>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h5 className="font-semibold text-gray-900 mb-3">💡 Workflow Tips:</h5>
                  <div className="text-sm text-gray-700 space-y-2">
                    <div><strong>Basic editing:</strong> Switch to Edit mode and click any highlighted field</div>
                    <div><strong>Bulk editing:</strong> Export CSV → Open in Excel/Google Sheets → Edit multiple values → Save → Import CSV</div>
                    <div><strong>Backup workflow:</strong> Export CSV before making major changes</div>
                    <div><strong>Template sharing:</strong> Export customized data to share with other CISOs</div>
                    <div><strong>Scenario planning:</strong> Create multiple CSV versions for different risk scenarios</div>
                  </div>
                </div>
                
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h5 className="font-semibold text-blue-900 mb-2">📋 Step-by-Step Bulk Editing:</h5>
                  <ol className="text-sm text-blue-800 space-y-1">
                    <li><strong>1.</strong> Click "Export CSV" to download your current data</li>
                    <li><strong>2.</strong> Open the downloaded file in Excel or Google Sheets</li>
                    <li><strong>3.</strong> Edit values in the "value" column (keep headers unchanged)</li>
                    <li><strong>4.</strong> Save the file as CSV format</li>
                    <li><strong>5.</strong> Click "Import CSV" and select your modified file</li>
                    <li><strong>6.</strong> All your changes will be applied instantly</li>
                  </ol>
                </div>
              </div>
            </div>
            
            <div className="sticky bottom-0 bg-white border-t p-6 flex justify-between items-center">
              <p className="text-xs text-gray-500">Press ESC to close • Scroll to see all content</p>
              <button 
                onClick={() => setShowImportExportHelp(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
      
      {!presentationMode && (
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-center mb-4">
              <div className="flex items-center gap-3">
                <div className="p-1">
                  <img 
                    src="/inp2-logo.png" 
                    alt="INP² Logo" 
                    className="w-12 h-12 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                  <div className="bg-blue-600 p-2 rounded-lg items-center justify-center" style={{display: 'none'}}>
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="text-center">
                  <h1 className="text-2xl font-bold text-gray-900">INP<sup>2</sup> Executive Cyber Bridge</h1>
                  <p className="text-gray-600">Bridging Cybersecurity & Executive Leadership</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <ModeSelector />
                
                <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={importFromCSV}
                      className="hidden"
                    />
                    <div 
                      className="px-3 py-1 rounded text-sm text-gray-600 hover:bg-gray-200 transition-colors flex items-center gap-1"
                      title="Import CSV files exported from this tool. Premium: Live sync from security platforms"
                    >
                      <Upload className="w-4 h-4" />
                      Import CSV
                    </div>
                  </label>
                  
                  <button
                    onClick={exportToCSV}
                    className="px-3 py-1 rounded text-sm text-gray-600 hover:bg-gray-200 transition-colors flex items-center gap-1"
                    title="Export your data for backup or bulk editing. Premium: Export live tool data"
                  >
                    <Download className="w-4 h-4" />
                    Export CSV
                  </button>
                </div>
                
                {/* Print Options */}
                <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => printSlide(currentSlide)}
                    className="px-3 py-1 rounded text-sm text-gray-600 hover:bg-gray-200 transition-colors flex items-center gap-1"
                    title="Print current slide"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    Print Slide
                  </button>
                  <button
                    onClick={() => printSlide()}
                    className="px-3 py-1 rounded text-sm text-gray-600 hover:bg-gray-200 transition-colors flex items-center gap-1"
                    title="Print complete board report"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Print Report
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <button
                  onClick={togglePresentationMode}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  Present
                </button>
                <span className="text-sm text-gray-600">
                  Slide {currentSlide + 1} of {slides.length}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {!presentationMode && (
        <div className="bg-white border-b px-6 py-3">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center">
              <div className="flex items-center gap-2">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`px-3 py-1 rounded text-sm whitespace-nowrap transition-colors ${
                      index === currentSlide
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {presentationMode ? (
        <div className="h-screen bg-white flex flex-col">
          <div className="flex items-center justify-between p-4 border-b bg-gray-50">
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-600" />
              <span className="text-sm font-medium text-gray-600">INP²</span>
            </div>
            <h2 className="text-xl font-bold text-gray-900 text-center flex-1">
              {slides[currentSlide].title}
            </h2>
            <div className="text-sm font-medium text-gray-600">
              {currentSlide + 1} / {slides.length}
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-8" style={{fontSize: '1.2rem', lineHeight: '1.5'}}>
            {slides[currentSlide].content}
          </div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="bg-white rounded-xl shadow-lg p-8 min-h-[600px]">
            <div className="mb-6 flex items-start gap-6">
              <div className="flex-shrink-0">
                <img 
                  src="/inp2-logo.png" 
                  alt="INP² Logo" 
                  className="w-12 h-12 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
                <div className="bg-blue-600 p-2 rounded-lg items-center justify-center" style={{display: 'none'}}>
                  <Shield className="w-8 h-8 text-white" />
                </div>
              </div>
              
              <div className="flex-1 min-w-0">
                <h2 className="text-3xl mb-2 font-bold text-gray-900">
                  {slides[currentSlide].title}
                </h2>
                <p className="text-lg text-gray-600">
                  {slides[currentSlide].subtitle}
                </p>
              </div>
            </div>

            <div className="mb-8" data-slide-index={currentSlide}>
              {slides[currentSlide].content}
            </div>

            <div className="flex items-center justify-between pt-6 border-t">
              <button
                onClick={prevSlide}
                disabled={currentSlide === 0}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>

              <div className="flex items-center gap-2">
                {slides.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === currentSlide ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={nextSlide}
                disabled={currentSlide === slides.length - 1}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {presentationMode && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 flex items-center gap-3 bg-black bg-opacity-80 text-white px-6 py-3 rounded-full shadow-lg">
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className="hover:bg-white hover:bg-opacity-20 p-2 rounded-full transition-colors disabled:opacity-30"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          
          <div className="flex items-center gap-2 mx-4">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index === currentSlide ? 'bg-white' : 'bg-white bg-opacity-40 hover:bg-opacity-60'
                }`}
              />
            ))}
          </div>
          
          <button
            onClick={nextSlide}
            disabled={currentSlide === slides.length - 1}
            className="hover:bg-white hover:bg-opacity-20 p-2 rounded-full transition-colors disabled:opacity-30"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          
          <div className="w-px h-6 bg-white bg-opacity-40 mx-2"></div>
          
          {/* Print current slide button */}
          <button
            onClick={() => printSlide(currentSlide)}
            className="hover:bg-white hover:bg-opacity-20 p-2 rounded-full transition-colors"
            title="Print current slide"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
          </button>
          
          {/* Print full report button */}
          <button
            onClick={() => printSlide()}
            className="hover:bg-white hover:bg-opacity-20 p-2 rounded-full transition-colors"
            title="Print complete board report"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </button>
          
          <div className="w-px h-6 bg-white bg-opacity-40 mx-2"></div>
          
          <button
            onClick={togglePresentationMode}
            className="hover:bg-red-600 bg-red-500 px-4 py-2 rounded-full text-sm font-medium transition-colors"
          >
            Exit
          </button>
        </div>
      )}

      {/* Presentation Mode Disclaimer */}
      {presentationMode && (
        <div className="fixed top-2 right-2 z-[9998]">
          <div className="bg-black bg-opacity-70 text-white text-xs px-3 py-1 rounded-full">
            © 2025 INP² - Educational Tool
          </div>
        </div>
      )}

      {/* Edit Mode Helper */}
      {!presentationMode && viewMode === 'edit' && (
        <div className="fixed bottom-4 left-4 bg-purple-100 p-3 rounded-lg shadow-lg border border-purple-200 max-w-xs">
          <p className="text-xs text-purple-800 font-medium">🎯 Edit Mode Active</p>
          <p className="text-xs text-purple-600">Click highlighted fields to customize your data</p>
          <p className="text-xs text-purple-600">Press Enter to save, Escape to cancel</p>
          <div className="mt-2 pt-2 border-t border-purple-200">
            <p className="text-xs text-purple-700 font-medium">💾 Bulk Editing:</p>
            <p className="text-xs text-purple-600">1. Export CSV → Edit in Excel → Import CSV</p>
            <p className="text-xs text-purple-600">2. Perfect for updating many values at once</p>
          </div>
          <div className="mt-2 pt-2 border-t border-purple-200">
            <p className="text-xs text-indigo-700 font-medium">⚡ Premium:</p>
            <p className="text-xs text-indigo-600">Auto-sync with Balbix, FAIR, SOAR, Nessus & more</p>
          </div>
        </div>
      )}

      {!presentationMode && (
        <div className="fixed bottom-4 right-4 bg-white p-3 rounded-lg shadow-lg border max-w-xs">
          <p className="text-xs text-gray-600 font-medium mb-1">🚀 CISO Dashboard Features:</p>
          <p className="text-xs text-gray-500">• Dynamic executive summaries based on risk data</p>
          <p className="text-xs text-gray-500">• Manual data entry (Premium: Live tool integrations)</p>
          <p className="text-xs text-gray-500">• Export CSV → Edit in Excel → Import CSV for bulk changes</p>
          <p className="text-xs text-gray-500">• Edit Q&A questions, answers, and statistics</p>
          <p className="text-xs text-gray-500">• Print individual slides or complete reports</p>
          <p className="text-xs text-gray-500">• Present mode for board meetings</p>
          <p className="text-xs text-gray-500">• Auto-calculated business KPIs</p>
          <div className="mt-2 pt-2 border-t border-gray-200">
            <p className="text-xs text-blue-600 font-medium">
              💡 Import/Export: 
              <button 
                onClick={() => setShowImportExportHelp(true)}
                className="underline hover:text-blue-800 ml-1 cursor-pointer"
              >
                click here for details
              </button>
            </p>
            <p className="text-xs text-indigo-600 font-medium">⚡ Premium: Direct integrations to security platforms</p>
          </div>
        </div>
      )}

      {/* Professional Disclaimer Footer */}
      {!presentationMode && (
        <div className="bg-gray-100 border-t mt-8">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
              <div className="flex items-start gap-3 mb-4">
                <div className="bg-blue-100 p-2 rounded-full flex-shrink-0">
                  <Shield className="w-4 h-4 text-blue-600" />
                </div>
                <div className="text-xs text-gray-700 leading-relaxed">
                  <strong className="text-gray-900">Professional Disclaimer:</strong> This educational tool provides strategic risk estimates using simplified methodology inspired by industry-standard FAIR practices. Actual incident costs vary significantly based on specific circumstances, industry, geography, and regulatory requirements. Results should complement, not replace, comprehensive risk assessments and professional advice.
                  <div className="mt-2 text-indigo-600">
                    <strong>Professional Version:</strong> Available with direct integrations to security platforms including Balbix, FAIR tools, SOAR, Nessus, and other enterprise security solutions.
                  </div>
                </div>
              </div>
              
              <div className="border-t pt-3 text-xs text-gray-600">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                  <div>
                    © 2025 INP² All rights reserved.
                  </div>
                  <div className="text-gray-500">
                    This Cybersecurity Risk Calculator and its underlying methodologies are proprietary to INP². No part of this tool may be reproduced, distributed, or used for commercial purposes without explicit written permission.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default INP2ExecutiveCyberBridge;
